import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  CssBaseline,
  ThemeProvider,
  createTheme,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";

import { getAllProjects, getProjectById } from "../../controllers/project.controller";
import HierarchyTree from "../../components/Projects/HierarchyTree";
import ClinicForm from "../../components/Projects/ClinicForm";
import { createClinic } from "../../controllers/clinic.controller";
import { useNavigate, useSearchParams } from "react-router-dom";

// ✅ Light / calm theme like your login
const appTheme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#2196f3" },
    background: { default: "#f6f9fc", paper: "#ffffff" },
  },
  shape: { borderRadius: 16 },
  typography: {
    fontFamily: ["IRANSans", "Vazirmatn", "Roboto", "Arial"].join(","),
  },
});

const ProjectHierarchyPage = () => {
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const [searchParams] = useSearchParams();
  const defaultProjectId = searchParams.get("projectId");

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedProjectTitle, setSelectedProjectTitle] = useState("");

  const [openDialog, setOpenDialog] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // UX states
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setPageError("");

    try {
      if (defaultProjectId) {
        const project = await getProjectById(defaultProjectId);
        if (project) {
          setProjects([project]);
          setSelectedProjectId(project._id);
          setSelectedProjectTitle(project.title?.fa || "---");
        } else {
          setProjects([]);
          setSelectedProjectId("");
          setSelectedProjectTitle("");
          setPageError("پروژه یافت نشد یا دسترسی ندارید.");
        }
      } else {
        const all = await getAllProjects();
        setProjects(all || []);
        // انتخاب پیش‌فرض: اولین پروژه
        if ((all || []).length > 0) {
          setSelectedProjectId(all[0]._id);
          setSelectedProjectTitle(all[0].title?.fa || "---");
        }
      }
    } catch (e) {
      console.error(e);
      setPageError("خطا در دریافت پروژه‌ها. لطفاً دوباره تلاش کنید.");
    } finally {
      setLoading(false);
    }
  }, [defaultProjectId]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const canShowTree = useMemo(() => !!selectedProjectId && !loading && !pageError, [
    selectedProjectId,
    loading,
    pageError,
  ]);

  const handleClinicSubmit = async (formData) => {
    try {
      await createClinic({ ...formData, projectId: selectedProjectId });
      setOpenDialog(false);
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      console.error("خطا در افزودن کلینیک:", err);
      setPageError(err?.message || "خطا در افزودن کلینیک");
    }
  };

  const handleProjectSelect = (e) => {
    const id = e.target.value;
    setSelectedProjectId(id);
    const project = projects.find((p) => p._id === id);
    setSelectedProjectTitle(project?.title?.fa || "---");
    // ریفرش درخت
    setRefreshKey((prev) => prev + 1);
  };

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />

      <Box sx={{ p: { xs: 1.5, sm: 3 }, direction: "rtl", minHeight: "100vh" }}>
        {/* Top Header Card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5 },
            mb: 2,
            borderRadius: 4,
            border: "1px solid",
            borderColor: "rgba(33,150,243,0.18)",
            background:
              "linear-gradient(135deg, rgba(33,150,243,0.14) 0%, rgba(33,150,243,0.04) 40%, rgba(255,255,255,1) 100%)",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant={isMobile ? "h6" : "h5"} fontWeight={900}>
                مدیریت کلینیک‌ها و صفحات
              </Typography>
              <Typography sx={{ color: "text.secondary", mt: 0.5, fontSize: 13 }}>
                ساختار پروژه → کلینیک → صفحه را مدیریت کنید و وارد فرم‌ساز شوید.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} justifyContent="flex-start">
              <Button
                variant="text"
                startIcon={<ArrowBackRoundedIcon />}
                onClick={() => navigate("/dashboard")}
                sx={{ fontWeight: 800 }}
              >
                بازگشت
              </Button>

              <Button
                variant="outlined"
                startIcon={<RefreshRoundedIcon />}
                onClick={fetchProjects}
                sx={{ fontWeight: 800 }}
              >
                بروزرسانی
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* Error */}
        {pageError && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 4,
              border: "1px solid",
              borderColor: "rgba(244,67,54,0.25)",
              bgcolor: "rgba(244,67,54,0.04)",
            }}
          >
            <Alert
              severity="error"
              action={
                <Button size="small" onClick={fetchProjects} sx={{ fontWeight: 800 }}>
                  تلاش مجدد
                </Button>
              }
            >
              {pageError}
            </Alert>
          </Paper>
        )}

        {/* Project Selector Card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5 },
            mb: 2,
            borderRadius: 4,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <ApartmentRoundedIcon color="primary" />
            <Typography fontWeight={900}>انتخاب پروژه</Typography>
          </Stack>

          {loading ? (
            <Box sx={{ mt: 1 }}>
              <Skeleton height={56} />
            </Box>
          ) : (
            <FormControl fullWidth>
              <InputLabel>پروژه</InputLabel>
              <Select value={selectedProjectId} onChange={handleProjectSelect} label="پروژه">
                {projects.map((proj) => (
                  <MenuItem key={proj._id} value={proj._id}>
                    {proj.title?.fa ?? "---"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {(!loading && projects.length === 0 && !pageError) && (
            <Alert sx={{ mt: 2 }} severity="warning">
              هیچ پروژه‌ای برای نمایش وجود ندارد.
            </Alert>
          )}
        </Paper>

        {/* Tree Card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: 4,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <AccountTreeRoundedIcon color="primary" />
              <Box>
                <Typography fontWeight={900}>
                  ساختار پروژه: {selectedProjectTitle || "---"}
                </Typography>
                <Typography sx={{ color: "text.secondary", fontSize: 12, mt: 0.3 }}>
                  روی «صفحه» کلیک کنید تا وارد فرم‌ساز شوید. ویرایش/حذف فقط از آیکون‌ها.
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} justifyContent="flex-start">
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={() => setOpenDialog(true)}
                disabled={!selectedProjectId || loading}
                sx={{ fontWeight: 900, py: 1.1, borderRadius: 3 }}
              >
                کلینیک جدید
              </Button>
            </Stack>
          </Stack>

          <Divider sx={{ my: 1.5 }} />

          {loading ? (
            <Stack spacing={1.2}>
              <Skeleton height={42} />
              <Skeleton height={42} />
              <Skeleton height={42} width="80%" />
            </Stack>
          ) : canShowTree ? (
            <HierarchyTree projectId={selectedProjectId} key={refreshKey} />
          ) : (
            !pageError && (
              <Alert severity="info">
                برای نمایش ساختار، یک پروژه را انتخاب کنید.
              </Alert>
            )
          )}

          {/* Optional small loading indicator under tree */}
          {loading && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2, color: "text.secondary" }}>
              <CircularProgress size={18} />
              <Typography sx={{ fontSize: 12 }}>در حال بارگذاری...</Typography>
            </Stack>
          )}
        </Paper>

        {/* Add Clinic Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 900 }}>افزودن کلینیک جدید</DialogTitle>
          <DialogContent>
            <ClinicForm onSubmit={handleClinicSubmit} onCancel={() => setOpenDialog(false)} />
          </DialogContent>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default ProjectHierarchyPage;
